import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Tags, Plus, Trash2, ArrowUp, ArrowDown, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";

export default function DomainesPP() {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newLabel, setNewLabel] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/pp/domains/all"); setRows(data.domains || []); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!newLabel.trim()) return;
    setBusy(true);
    try { await api.post("/pp/domains", { label: newLabel.trim() }); setNewLabel(""); toast.success(t("pp.domainsMgmt.added")); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    setBusy(false);
  };
  const rename = async (d, label) => {
    try { await api.put(`/pp/domains/${d.id}`, { label }); setRows((r) => r.map((x) => (x.id === d.id ? { ...x, label } : x))); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const toggle = async (d) => {
    try { await api.put(`/pp/domains/${d.id}`, { label: d.label, active: !d.active }); setRows((r) => r.map((x) => (x.id === d.id ? { ...x, active: !x.active } : x))); toast.success(t("pp.domainsMgmt.updated")); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const del = async (d) => {
    if (!window.confirm(t("pp.domainsMgmt.deleteConfirm"))) return;
    try { await api.delete(`/pp/domains/${d.id}`); toast.success(t("pp.domainsMgmt.deleted")); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const move = async (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= rows.length) return;
    const next = [...rows];
    [next[i], next[j]] = [next[j], next[i]];
    setRows(next);
    try { await api.post("/pp/domains/reorder", { order: next.map((x) => x.id) }); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  return (
    <div className="min-h-screen bg-[#0a1721] text-slate-100 p-4 sm:p-6" data-testid="domaines-page">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-2 text-teal-400"><Tags className="h-5 w-5" /><span className="text-xs font-semibold uppercase tracking-widest">CarePilot</span></div>
        <h1 className="text-3xl sm:text-4xl font-extrabold mt-1">{t("pp.domainsMgmt.title")}</h1>
        <p className="text-slate-400 text-sm mt-1 mb-6">{t("pp.domainsMgmt.subtitle")}</p>

        <div className="flex gap-2 mb-5">
          <Input value={newLabel} onChange={(e) => setNewLabel(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()}
            placeholder={t("pp.domainsMgmt.newPlaceholder")} className="bg-white/[0.04] border-white/10" data-testid="domain-new-input" />
          <Button onClick={add} disabled={busy || !newLabel.trim()} data-testid="domain-add-btn" className="bg-teal-500 hover:bg-teal-400 text-slate-900 font-semibold shrink-0"><Plus className="h-4 w-4 mr-1" />{t("pp.domainsMgmt.add")}</Button>
        </div>

        {loading ? (
          <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-teal-400" /></div>
        ) : rows.length === 0 ? (
          <p className="text-slate-500 text-center py-10">{t("pp.domainsMgmt.empty")}</p>
        ) : (
          <div className="space-y-2">
            {rows.map((d, i) => (
              <div key={d.id} className={`flex items-center gap-2 rounded-xl ring-1 ring-white/10 p-2.5 ${d.active ? "bg-white/[0.04]" : "bg-white/[0.02] opacity-70"}`} data-testid={`domain-row-${i}`}>
                <div className="flex flex-col">
                  <button onClick={() => move(i, -1)} disabled={i === 0} className="text-slate-500 hover:text-teal-300 disabled:opacity-30" data-testid={`domain-up-${i}`}><ArrowUp className="h-3.5 w-3.5" /></button>
                  <button onClick={() => move(i, 1)} disabled={i === rows.length - 1} className="text-slate-500 hover:text-teal-300 disabled:opacity-30" data-testid={`domain-down-${i}`}><ArrowDown className="h-3.5 w-3.5" /></button>
                </div>
                <Input defaultValue={d.label} onBlur={(e) => e.target.value.trim() && e.target.value !== d.label && rename(d, e.target.value.trim())}
                  className="flex-1 bg-transparent border-white/10" data-testid={`domain-label-${i}`} />
                <label className="flex items-center gap-1.5 text-xs text-slate-400 shrink-0">
                  <Switch checked={d.active} onCheckedChange={() => toggle(d)} data-testid={`domain-active-${i}`} />
                  {d.active ? t("pp.domainsMgmt.active") : t("pp.domainsMgmt.inactive")}
                </label>
                <button onClick={() => del(d)} className="p-2 rounded-lg text-slate-400 hover:text-red-300 hover:bg-white/5 shrink-0" data-testid={`domain-del-${i}`}><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
